import {
  initVersionedSchema,
  RawInlineNodes,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  IAppointment,
  IBrand,
  ILabJob,
  ILabJobType,
  IPatient,
  IPractice,
  IStaffer,
  LabJobStatus,
  MentionResourceType,
  PracticeCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  CollectionReference,
  DATE_TIME_FORMAT,
  DocumentReference,
  Firestore,
  INamedDocument,
  initFirestoreModel,
  IReffable,
  MockTimestamp,
  reduceToSingleArray,
  safeCombineLatest,
  subCollection,
  toMoment,
  toMomentTz,
  toTimestamp,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TimezoneResolver } from '../../timezone';
import { Brand } from '../brand';
import { toMention } from '../mention/mention';
import { WorkflowItem } from '../workflow-item';

export const STATUS_TO_TIMESTAMP_FIELD: Partial<
  Record<LabJobStatus, 'sentAt' | 'receivedAt' | 'issuedAt'>
> = {
  [LabJobStatus.Sent]: 'sentAt',
  [LabJobStatus.Received]: 'receivedAt',
  [LabJobStatus.Issued]: 'issuedAt',
};

export class LabJobType {
  static init(overrides: AtLeast<ILabJobType, 'name'>): ILabJobType {
    return {
      cost: 0,
      createdAt: MockTimestamp(),
      updatedAt: MockTimestamp(),
      deleted: false,
      ...overrides,
    };
  }
}

export class LabJob extends WorkflowItem {
  static init(
    overrides: AtLeast<ILabJob, 'lab' | 'patient' | 'type'>
  ): ILabJob {
    return {
      status: LabJobStatus.Sending,
      statusHistory: [],
      cost: 0,
      title: [],
      description: initVersionedSchema(),
      interactions: [],
      ...initFirestoreModel(),
      ...overrides,
    };
  }

  static col(practice: IReffable<IPractice>): CollectionReference<ILabJob> {
    return subCollection<ILabJob>(practice.ref, PracticeCollection.LabJobs);
  }

  static all$(practice: IReffable<IPractice>): Observable<WithRef<ILabJob>[]> {
    return Firestore.query$(undeletedQuery(LabJob.col(practice)));
  }

  static appointment$(
    labJob: ILabJob
  ): Observable<WithRef<IAppointment> | undefined> {
    if (!labJob.appointment) {
      return of(undefined);
    }
    return Firestore.doc$(labJob.appointment.ref);
  }

  static practice$(labJob: WithRef<ILabJob>): Observable<WithRef<IPractice>> {
    return Firestore.doc$(Firestore.getParentDocRef<IPractice>(labJob.ref));
  }

  static practiceRef(labJob: WithRef<ILabJob>): DocumentReference<IPractice> {
    return Firestore.getParentDocRef<IPractice>(labJob.ref);
  }

  static byPatient$(
    practice: IReffable<IPractice>,
    patientRef: DocumentReference<IPatient>
  ): Observable<WithRef<ILabJob>[]> {
    return Firestore.query$(
      undeletedQuery(LabJob.col(practice)),
      where('patient.ref', '==', patientRef)
    );
  }

  static byBrand$(brand: IReffable<IBrand>): Observable<WithRef<ILabJob>[]> {
    return from(Firestore.getDocs(Brand.practiceCol(brand))).pipe(
      switchMap((practices) =>
        safeCombineLatest(practices.map((practice) => LabJob.all$(practice)))
      ),
      map(reduceToSingleArray)
    );
  }

  static late(item: ILabJob): boolean {
    if (!item.dueDate || LabJob.complete(item)) {
      return false;
    }

    return moment().isAfter(toMoment(item.dueDate));
  }

  static complete(labJob: ILabJob): boolean {
    return [LabJobStatus.Received, LabJobStatus.Issued].includes(labJob.status);
  }

  static isOverdue(labJob: ILabJob): boolean {
    const now = moment();
    return (
      !LabJob.complete(labJob) &&
      now.isSameOrAfter(toMoment(labJob.dueDate ?? now), 'day')
    );
  }

  static async generateTitle(labJob: ILabJob): Promise<RawInlineNodes> {
    const titleContent: RawInlineNodes = [toTextContent(labJob.type.name)];

    if (labJob.patient) {
      titleContent.push(
        toTextContent(' for '),
        toMentionContent(toMention(labJob.patient, MentionResourceType.Patient))
      );
    }

    if (!labJob.appointment) {
      return titleContent;
    }

    const appointment = await Firestore.getDoc(labJob.appointment.ref);

    if (appointment.event) {
      const timezone = await TimezoneResolver.fromEvent(appointment);
      const appointmentDescription = toMomentTz(
        appointment.event.from,
        timezone
      ).format(DATE_TIME_FORMAT);
      titleContent.push(toTextContent(` for ${appointmentDescription}`));
    }

    return titleContent;
  }

  static updateStatus(labJob: ILabJob, status: LabJobStatus): void {
    if (labJob.status === status) {
      return;
    }
    const now = toTimestamp();
    labJob.statusHistory.push({
      status: labJob.status,
      updatedAt: now,
    });
    labJob.status = status;

    const field = STATUS_TO_TIMESTAMP_FIELD[status];
    if (field && !labJob[field]) {
      labJob[field] = now;
    }
    if (status === LabJobStatus.Issued && !labJob.completedDate) {
      labJob.completedDate = now;
    }
  }

  static async hasConflict(labJob: ILabJob): Promise<boolean> {
    if (LabJob.complete(labJob)) {
      return false;
    }

    if (!labJob.appointment) {
      return false;
    }

    const appointment = await Firestore.getDoc(labJob.appointment.ref);

    if (!appointment.event) {
      return false;
    }
    const timezone = await TimezoneResolver.fromEvent(appointment);
    const within1Day: boolean =
      toMomentTz(appointment.event.from, timezone).diff(
        moment.tz(timezone),
        'days',
        true
      ) < 1;

    if (within1Day && labJob.status === LabJobStatus.Received) {
      return false;
    }

    return within1Day;
  }

  /**
   * Returns the practitioner shared by ALL non-deleted appointments in the
   * supplied window. If the appointments span multiple practitioners, are all
   * deleted, or contain no practitioner refs, returns undefined.
   */
  static inferUniquePractitioner(
    appointments: WithRef<IAppointment>[]
  ): INamedDocument<IStaffer> | undefined {
    const qualifying = appointments.filter(
      (appointment) => !appointment.deleted && appointment.practitioner?.ref
    );

    if (!qualifying.length) {
      return;
    }

    const uniquePractitioners = uniqBy(
      qualifying.map((appointment) => appointment.practitioner),
      (practitioner) => practitioner.ref.path
    );

    if (uniquePractitioners.length !== 1) {
      return;
    }

    return uniquePractitioners[0];
  }

  /**
   * Resolves the practitioner the Lab Job form should auto-populate from a
   * given appointment selection. Returns the appointment's own practitioner
   * when present, otherwise falls back to a unique practitioner inferred
   * from the patient's recent appointments. Returns undefined when the
   * appointment is cleared so that prior selections are preserved.
   */
  static derivePractitioner(
    appointment: WithRef<IAppointment> | undefined,
    recentAppointments: WithRef<IAppointment>[]
  ): INamedDocument<IStaffer> | undefined {
    if (!appointment) {
      return;
    }
    if (appointment.practitioner?.ref && !appointment.deleted) {
      return appointment.practitioner;
    }
    return LabJob.inferUniquePractitioner(recentAppointments);
  }
}
